/**
 * Lightweight i18n for the Customer Identity admin UI.
 *
 * Why a small in-house module instead of `react-i18next`:
 *   - No new dependency or provider boilerplate.
 *   - Ships with the bundle, works inside any React component, and outside
 *     React (e.g. inside the API error handler).
 *
 * Usage:
 *   const t = useCITranslate();
 *   t('users.addUser');                     // "Add user"
 *   t('users.deleteConfirm', { email });    // "Remove user "..."? This cannot ..."
 *   t('errors.user_email_already_exists');  // "A user with this email ..."
 *
 * Or, outside React:
 *   import { tCI } from '../../i18n/customerIdentity';
 *   tCI('errors.request_failed');
 *
 * To add a language:
 *   1. Copy `en.js` to `<locale>.js` and translate values.
 *   2. Register it in `dictionaries` below.
 *   3. Optionally call `setCILocale('<locale>')` from a settings UI.
 *
 * Locale resolution order:
 *   1. `localStorage.ci_locale` if set and supported.
 *   2. The first 2 letters of `navigator.language` if supported.
 *   3. English (`en`).
 */
import { useEffect, useState } from 'react';

import en from './en';

const dictionaries = {
  en,
};

const STORAGE_KEY = 'ci_locale';
const FALLBACK = 'en';

const listeners = new Set();

let _currentLocale = null;

const detectLocale = () => {
  try {
    const stored =
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && dictionaries[stored]) return stored;
  } catch (e) {
    // localStorage may be unavailable (SSR, sandboxed iframe). Ignore.
  }
  try {
    const nav =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language.slice(0, 2)
        : FALLBACK;
    if (dictionaries[nav]) return nav;
  } catch (e) {
    // Same as above.
  }
  return FALLBACK;
};

const ensureLocale = () => {
  if (_currentLocale) return _currentLocale;
  _currentLocale = detectLocale();
  return _currentLocale;
};

const getValue = (dict, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), dict);

const interpolate = (str, params) => {
  if (typeof str !== 'string' || !params) return str;
  return str.replace(/\{(\w+)\}/g, (_match, key) => {
    const v = params[key];
    return v === undefined || v === null ? `{${key}}` : String(v);
  });
};

/**
 * Resolve a translation key against the current locale, falling back to
 * English and then to the key itself if missing in both.
 *
 * Always safe — returns a string, never throws.
 */
export const tCI = (key, params) => {
  if (!key) return '';
  const locale = ensureLocale();
  const primary = getValue(dictionaries[locale], key);
  if (primary !== undefined) return interpolate(primary, params);
  if (locale !== FALLBACK) {
    const fallback = getValue(dictionaries[FALLBACK], key);
    if (fallback !== undefined) return interpolate(fallback, params);
  }
  return key;
};

/** Returns the active locale (e.g. `'en'`). */
export const getCILocale = () => ensureLocale();

/**
 * Switch the active locale. Persists to localStorage so the choice survives
 * a refresh. No-ops for unknown locales.
 */
export const setCILocale = (locale) => {
  if (!dictionaries[locale]) return;
  _currentLocale = locale;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, locale);
  } catch (e) {
    // Ignore storage failures.
  }
  listeners.forEach((listener) => {
    try {
      listener(locale);
    } catch (e) {
      // A misbehaving subscriber must not break the rest.
    }
  });
};

/** Returns the list of supported locale codes. */
export const getSupportedCILocales = () => Object.keys(dictionaries);

/**
 * React hook that returns a translate function and re-renders the component
 * whenever the locale changes via `setCILocale`.
 */
export const useCITranslate = () => {
  const [locale, setLocale] = useState(ensureLocale());
  useEffect(() => {
    const listener = (next) => setLocale(next);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  // Returning a closure that captures `locale` so React knows to re-render
  // dependent components when the locale changes.
  return (key, params) => {
    void locale;
    return tCI(key, params);
  };
};

/**
 * Translate a structured error object thrown by `customerIdentityApi.js`.
 * Accepts:
 *   - `CIError` instance (`{ code, params, message }`),
 *   - plain string (treated as a key first, then literal),
 *   - undefined → returns generic `errors.request_failed` message.
 */
export const translateCIError = (err) => {
  if (!err) return tCI('errors.request_failed');
  if (typeof err === 'string') {
    const direct = tCI(`errors.${err}`);
    if (direct && direct !== `errors.${err}`) return direct;
    return err;
  }
  const code = err.code || err.message;
  if (!code) return tCI('errors.request_failed');
  const lookupKey = `errors.${code}`;
  const translated = tCI(lookupKey, err.params);
  if (translated && translated !== lookupKey) return translated;
  // Last resort: the human-readable `message` from CIError, otherwise the code.
  return err.message || code;
};
